// Added by Sejune Oh on 2019-02-18
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import axios from 'axios'
import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { Component, createRef } from 'react'
import { Redirect, RouterProps } from 'react-router'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import {
  AUTH_TOKEN_COOKIE,
  CACHED_EMAIL,
  HTTP_HEADER_AUTH_KEY,
  KEEP_ME_LOGGED_IN_COOKIE,
  REALEASED_VERSION,
  ReactClickEvent,
  ReactEmptyEvent,
  ReactInputChangeEvent,
  ReactInputCheckedEvent,
  ReactKeyUpEvent,
  StopServerReason,
  routerPath,
} from '@/helper/define'

// Intl
import Intl from '@/helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Store
import CLAppStore from '@/stores/CLAppStore'
import EventStore from '@/stores/EventStore'
import ProductStore from '@/stores/ProductStore'
import { HistoryStore } from '@/stores/global'
import { UserAuthStore } from '@/stores/user/UserAuth'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Component
import Dialog from '@/components/common/Dialog'
import { ResetUserPassword, SystemAdminAdd } from '@/components/mgmt/users'
import { deleteCookie, getCookie, setCookie } from '@/helper/util'
import { PackageConfigStore } from '@/stores/mgmt/config'
import LoginForm from './LoginForm'
import MobileLoginForm from './MobileLoginForm'
import TwoFactorAuthDialog from './TwoFactorAuthDialog'
import TwoFactorRegisterDialog from './TwoFactorRegisterDialog'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface LoginFormCProps extends RouterProps {
  userAuth?: UserAuthStore
  globalEvt?: EventStore
  historyCtr?: HistoryStore
  product?: ProductStore
  packageConfig?: PackageConfigStore
  isMobile: boolean
  clApp?: CLAppStore
}

interface UserAccountProps {
  email: string
  password: string
  [key: string]: string
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
@inject('userAuth', 'globalEvt', 'historyCtr', 'product', 'packageConfig', 'clApp')
@observer
class LoginFormContainer extends Component<LoginFormCProps> {
  _isChecked: boolean = false
  _isErrorOccured: boolean = false
  _dialogType: string = ''
  _errMessage: string = ''
  _hidePassword: boolean = true
  _isOnLoading: boolean = false
  _formId = 'c-i-d'
  _twoFactorAuthData: { url: string; secret: string; code: string } = { url: '', secret: '', code: '' }
  _handleDialogConfirm?: () => Promise<{}>
  _handleDialogCancel?: () => Promise<{}>
  _getTwoFactorData?: () => Promise<boolean | { url: string; secret: string; code: string }>
  _handleReqTwoFactorRegister?: () => Promise<{}>
  _handleTwoFactorRegisterClose?: () => Promise<{}>
  _handleReqTwoFactorAuth?: () => Promise<{}>

  _buttonId = 'login-btn'
  _account: UserAccountProps = {
    email: '',
    password: '',
  }
  _version: string = process.env.REACT_APP_VER_RSC_TEXTS || REALEASED_VERSION
  _dialogRef = createRef<any>()
  _twoFactorRegisterDialogRef = createRef<Dialog>()
  _twoFactorAuthDialogRef = createRef<Dialog>()

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  constructor(props: LoginFormCProps) {
    super(props)
    makeObservable(this, {
      _isChecked: observable,
      _isErrorOccured: observable,
      _errMessage: observable,
      _dialogType: observable,
      _hidePassword: observable,
      _isOnLoading: observable,
      _handleDialogConfirm: observable,
      _handleDialogCancel: observable,
      _checkCachedEmail: action,
      _handleSnackbarClose: action,
      _handleInputCheckedChange: action,
      _handleLoginBtnClick: action,
      _handlePasswordInputIconClick: action,
      _preCheckToSignIn: action,
      _handleExpiredUser: action,
      _handleInvalidParam: action,
      userAuth: computed,
      product: computed,
      componentOptions: computed,
    })
    this._checkCachedEmail()
  }

  componentDidMount() {
    // ! Input의 Label과 Border의 위치가 어긋나는 이슈가 있음. 강제적으로 Rerender하는 로직이 필요.
    setTimeout(() => {
      this.forceUpdate()

      if (this.props.userAuth!.isExpired) {
        this._handleExpiredUser()
        return
      }

      if (this.props.userAuth!.isInvalidParam) {
        this._handleInvalidParam()
        return
      }
    }, 500)
  }

  render() {
    if (this.product.needSystemAdmin) {
      return <SystemAdminAdd />
    }

    if (this.userAuth.isSignedUp) {
      if (!this.props.globalEvt!.isConfigLoaded) return null

      if (this.userAuth.isPasswordInitializationRequired) {
        return <ResetUserPassword history={this.props.history} />
      }

      const prevPath = this.props.historyCtr!.dequeueHistory()
      const dashboardEnable = this.props.packageConfig!.checkDashboardEnable()
      const useDashboardAsLanding = this.props.packageConfig!.checkDashboardIsLanding()
      const redirectPath =
        dashboardEnable && useDashboardAsLanding ? `${routerPath.DASHBOARD}` : `${routerPath.WORKLIST}`
      return <Redirect to={prevPath || redirectPath} />
    }

    return (
      <>
        <Dialog ref={this._dialogRef} onConfirm={this._handleDialogConfirm} onCancel={this._handleDialogCancel} />
        <TwoFactorRegisterDialog
          dialogRef={this._twoFactorRegisterDialogRef}
          onCancel={this._handleDialogCancel}
          onConfirm={this._handleReqTwoFactorRegister}
          getTwoFactorData={this._getTwoFactorData}
        />
        <TwoFactorAuthDialog
          dialogRef={this._twoFactorAuthDialogRef}
          onCancel={this._handleDialogCancel}
          onConfirm={this._handleReqTwoFactorAuth}
          auth2_data={this._twoFactorAuthData}
        />
        {this.props.isMobile ? (
          <MobileLoginForm {...this.componentOptions} />
        ) : (
          <LoginForm {...this.componentOptions} />
        )}
      </>
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Pre-hook for checking cached user email
  _checkCachedEmail = () => {
    if (!this.product.enableKeepUserLoggedIn) {
      localStorage.setItem(CACHED_EMAIL, '')
      return
    }

    const cachedEmail = localStorage.getItem(CACHED_EMAIL)
    if (!cachedEmail) return

    this._isChecked = true
    this._account.email = cachedEmail
  }

  _handleSnackbarClose: ReactEmptyEvent = () => {
    this._isErrorOccured = false
  }

  _handleInputCheckedChange: ReactInputCheckedEvent = (event, checked) => {
    this._isChecked = checked

    if (this._isChecked) {
      localStorage.setItem(CACHED_EMAIL, this._account.email)
    } else {
      localStorage.removeItem(CACHED_EMAIL)
      deleteCookie(KEEP_ME_LOGGED_IN_COOKIE)
    }
  }

  _handleLoginBtnClick: ReactEmptyEvent = async () => {
    try {
      this._isOnLoading = true
      if (this._account.email === '') {
        this._dialogType= ''
        this._errMessage = this.userAuth.getSignInErrMessage(Intl, 'idNotEmpty')
        this._isErrorOccured = true
        return
      }

      if (this._account.password === '') {
        this._dialogType= ''
        this._errMessage = this.userAuth.getSignInErrMessage(Intl, 'pwNotEmpty')
        this._isErrorOccured = true
        return
      }

      const isOkayToGo = await this._preCheckToSignIn(this._account.email)

      if (!isOkayToGo) {
        this._isOnLoading = false
        return
      }

      if (this._isChecked) {
        setCookie({
          name: KEEP_ME_LOGGED_IN_COOKIE,
          value: 'true',
          expHours: 365 * 24,
        })
      } else {
        deleteCookie(KEEP_ME_LOGGED_IN_COOKIE)
      }

      const { authToken, userInfo } = await this.userAuth.checkUserInfo({
        ...this._account,
        keep_me_logged_in: this._isChecked,
      })
      // 2차 인증 활성화 여부 확인
      if (userInfo.auth2_enabled) {
        // 2차 인증 활성화 후 최초 인증이 아님
        if (userInfo.auth2_data) {
          const isTwoFactorAuthSuccess = await this._handleTwoFactorAuthDialog(
            authToken.access_token,
            userInfo.auth2_data,
            userInfo.id!
            )
            if (!isTwoFactorAuthSuccess) {
              this._isOnLoading = false
              return
            }
          }
        // 2차 인증 활성화 후 최초 인증
        else {
          const isTwoFactorRegisterSuccess = await this._handleTwoFactorRegisterDialog(
            authToken.access_token,
            userInfo.id!
          )
          if (!isTwoFactorRegisterSuccess) {
            this._isOnLoading = false
            return
          }
        }
      }

      await this.userAuth.signIn(authToken)
    } catch (errMsg) {
      this._dialogType= ''
      this._errMessage = this.userAuth.getSignInErrMessage(Intl, errMsg)
      this._isErrorOccured = true
    } finally {
      this._isOnLoading = false
    }
  }

  _handlePasswordInputIconClick: ReactClickEvent = () => {
    this._hidePassword = !this._hidePassword
  }

  _preCheckToSignIn = (curId: string) => {
    return new Promise((mainResolve) => {
      const authTokenCookie = getCookie(AUTH_TOKEN_COOKIE)
      if (!authTokenCookie) return mainResolve(true)

      const { access_token } = JSON.parse(authTokenCookie)
      const { username } = this.props.userAuth!.decodeUserInfo(access_token)!

      // # Set Auth token
      axios.defaults.headers.common[HTTP_HEADER_AUTH_KEY] = `Bearer ${access_token}`

      const isAppServerRunnging = this.props.clApp!.isAppServerRunnging(username)
      if (!isAppServerRunnging) return mainResolve(true)

      // let idFromToken = validator.isEmail(curId) ? email : username

      this._handleDialogConfirm = () => {
        return new Promise(async (resolve) => {
          await this.props.clApp!.stopAppServer(true, StopServerReason.NEW_SESSION_STARTED, username)
          mainResolve(true)
          resolve(true)
        })
      }

      this._handleDialogCancel = () => {
        return new Promise((resolve) => {
          mainResolve(false)
          resolve(true)
        })
      }

      this._dialogRef
        .current!.setDialogInfo({
          // prettier-ignore
          title: Intl.getIntlText({ id: 'htmljs_react.login.loginformcontainer.dialog_title_signed_in' }),
          // prettier-ignore
          description: Intl.getIntlText({ id: 'htmljs_react.login.loginformcontainer.dialog_desc_signed_in' }),
          // prettier-ignore
          confirmBtn: Intl.getIntlText({ id: 'htmljs_react.login.loginformcontainer.dialog_confirm_btn_signed_in' }),
          disableBackdropClick: true,
        })
        .handleOpen()
    })
  }

  _handleTwoFactorRegisterDialog = (access_token: string, id: string) => {
    return new Promise((mainResolve) => {
      axios.defaults.headers.common[HTTP_HEADER_AUTH_KEY] = `Bearer ${access_token}`

      this._getTwoFactorData = async () => {
        return new Promise(async (resolve) => {
          try{
            const auth2Data = await this.userAuth.getTwoFactorData(id)
            resolve(auth2Data) 
          } catch (err) {
            resolve(false)
          }
        })
      }

      this._handleReqTwoFactorRegister = () => {
        return new Promise(async (resolve) => {
          //Register 하는 로직
          let customInfoElement
          let e: Error | null = null
          try {
            customInfoElement = this._twoFactorRegisterDialogRef.current!.state
          } catch (err) {
            e = err
          } finally {
            const result = await this.userAuth.twoFactorRegister(customInfoElement, id)
            if (!result) {
              resolve(false)
              this._errMessage = this.userAuth.getSignInErrMessage(Intl, 'wTwoFactorCode')
              this._dialogType = 'twoFactorRegister'
              this._isErrorOccured = true
              return 
            }
            mainResolve(true)
            resolve(true)
            return
          }
        })
      }

      this._handleDialogCancel = () => {
        return new Promise((resolve) => {
          mainResolve(false)
          resolve(true)
        })
      }
      
      this._twoFactorRegisterDialogRef
        .current!.setDialogInfo({
          // prettier-ignore
          title: Intl.getIntlText({ id: 'htmljs_react.login.loginformcontainer.dialog_title_google_2fa_register' }),
          // prettier-ignore
          confirmBtn: Intl.getIntlText({ id: 'htmljs_react.login.loginformcontainer.dialog_register_btn_signed_in' }),
          disableBackdropClick: true
        })
        .setShouldOpenOnConfirm(true)
        .handleOpen()
    })
  }

  _handleTwoFactorAuthDialog = (access_token: string, auth2_data: string, id: string) => {
    return new Promise((mainResolve) => {
      axios.defaults.headers.common[HTTP_HEADER_AUTH_KEY] = `Bearer ${access_token}`
      this._twoFactorAuthData = JSON.parse(auth2_data)

      this._handleReqTwoFactorAuth = () => {
        return new Promise(async (resolve) => {
          //auth 하는 로직
          let customInfoElement
          let e: Error | null = null

          try {
            customInfoElement = this._twoFactorAuthDialogRef.current!.state
          } catch (err) {
            e = err
          } finally {
            const result = await this.userAuth.twoFactorAuth(customInfoElement, id)
            if (!result) {
              resolve(false)
              this._errMessage = this.userAuth.getSignInErrMessage(Intl, 'wTwoFactorCode')
              this._dialogType = 'twoFactorAuth'
              this._isErrorOccured = true
              return
            }
            mainResolve(true)
            resolve(true)
            return
          }
        })
      }

      this._handleDialogCancel = () => {
        return new Promise((resolve) => {
          mainResolve(false)
          resolve(true)
        })
      }
      this._twoFactorAuthDialogRef
        .current!.setDialogInfo({
          // prettier-ignore
          title: Intl.getIntlText({ id: 'htmljs_react.login.loginformcontainer.dialog_title_google_2fa_auth' }),
          disableBackdropClick: true
        })
        .setShouldOpenOnConfirm(true)
        .handleOpen()
    })
  }

  _handleExpiredUser = () => {
    this.props.historyCtr!.clearHistory()

    // prettier-ignore
    const title = Intl.getIntlText({ id: 'htmljs_react.login.loginformcontainer.dialog_title_invalid_param' })
    // prettier-ignore
    const description = Intl.getIntlText({ id: 'htmljs_react.login.loginformcontainer.dialog_desc_link_expired' })
    // prettier-ignore
    const confirmBtn = Intl.getIntlText({ id: 'htmljs_react.login.loginformcontainer.dialog_btn_confirm' })
    this.props.globalEvt!.openDialog(
      {
        disableBackdropClick: true,
        title,
        description,
        confirmBtn,
        hideCancelBtn: true,
      },
      () => {
        return new Promise((resolve) => {
          this.props.userAuth!.clearExpiredFlag()
          resolve(true)
        })
      }
    )
  }

  _handleInvalidParam = () => {
    this.props.historyCtr!.clearHistory()

    // prettier-ignore
    const title = Intl.getIntlText({ id: 'htmljs_react.login.loginformcontainer.dialog_title_invalid_param' })
    // prettier-ignore
    const description = Intl.getIntlText({ id: 'htmljs_react.login.loginformcontainer.dialog_desc_link_not_found' })
    // prettier-ignore
    const confirmBtn = Intl.getIntlText({ id: 'htmljs_react.login.loginformcontainer.dialog_btn_confirm' })
    this.props.globalEvt!.openDialog(
      {
        disableBackdropClick: true,
        title,
        description,
        confirmBtn,
        hideCancelBtn: true,
      },
      () => {
        return new Promise((resolve) => {
          this.props.userAuth!.clearInvalidParamFlag()
          resolve(true)
        })
      }
    )
  }

  get userAuth() {
    return this.props.userAuth!
  }

  get product() {
    return this.props.product!
  }

  get componentOptions() {
    return {
      loginBtnId: this._buttonId,
      savedEmail: this._account.email,
      errMessage: this._errMessage,
      dialogType:this._dialogType,
      version: this._version,
      copyRight: this.props.product!.copyright,
      isChecked: this._isChecked,
      isErrorOccured: this._isErrorOccured,
      onValueChange: this._handleInputValueChange,
      onCheckedChange: this._handleInputCheckedChange,
      onBtnClick: this._handleLoginBtnClick,
      onSnackbarClose: this._handleSnackbarClose,
      onKeyUp: this._handleKeyUp,
      hidePassword: this._hidePassword,
      onPasswordInputIconClick: this._handlePasswordInputIconClick,
      leftSectionBackgroundImage: this.product.loginLeftSectionBackgroundImage,
      useLoginFormTopImage: this.product.useLoginFormTopImage,
      productBI: this.product.productBI,
      loginTopImage: this.product.loginTopImage,
      isOnLoading: this._isOnLoading,
      enableKeepSigninStatus: this.product.enableKeepUserLoggedIn,
      siteName: this.product.siteName,
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _handleInputValueChange: ReactInputChangeEvent = ({ currentTarget: { value, name } }) => {
    this._account[name] = value

    if (name === 'email' && this._isChecked) {
      localStorage.setItem(CACHED_EMAIL, this._account.email)
    }
  }

  _handleKeyUp: ReactKeyUpEvent = (event) => {
    if (event.keyCode !== 13) return

    const $btn = document.getElementById(this._buttonId) as HTMLButtonElement
    $btn.click()
  }
}

export default LoginFormContainer